// /feedback — owner+manager dashboard hlasenii uzivatelu.
// Design konzistentni s Automatizace/AuditLog: KPI dlazdice + filter pills + tabulka.

import { confirmDialog } from "../components/confirmHost.jsx";
import React, { useMemo, useState } from "react";
import { Bug, Lightbulb, MessageSquare, CheckCircle, Clock, XCircle, RefreshCw, Trash2, ExternalLink, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import GradientHeader from "../components/GradientHeader.jsx";
import { useApiData, apiFetch } from "../lib/api.js";
import { formatDateTime } from "../lib/format.js";
import { toast } from "../components/Toast.jsx";
import { Oddelovac } from "../components/ui.jsx";
import FotkaLightbox from "../components/FotkaLightbox.jsx";

const TYP_META = {
  bug: { label: "Bug", icon: Bug, color: "rose" },
  navrh: { label: "Návrh", icon: Lightbulb, color: "amber" },
  zpetna_vazba: { label: "Zpětná vazba", icon: MessageSquare, color: "sky" },
};

const STATUS_META = {
  open: { label: "Otevřené", color: "rose" },
  in_progress: { label: "V řešení", color: "amber" },
  resolved: { label: "Vyřešeno", color: "emerald" },
  wont_fix: { label: "Neřešit", color: "slate" },
};

const STATUS_PILL = {
  rose: "bg-rose-100 text-rose-700 border-rose-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  sky: "bg-sky-100 text-sky-700 border-sky-200",
};

function KpiBox({ icon: Icon, color, label, value, onClick, active }) {
  const bg = color === "rose" ? "from-rose-50 to-rose-100" :
             color === "amber" ? "from-amber-50 to-amber-100" :
             color === "emerald" ? "from-emerald-50 to-emerald-100" :
             color === "sky" ? "from-sky-50 to-sky-100" :
             "from-slate-50 to-slate-100";
  const text = color === "rose" ? "text-rose-700" :
               color === "amber" ? "text-amber-700" :
               color === "emerald" ? "text-emerald-700" :
               color === "sky" ? "text-sky-700" :
               "text-slate-700";
  return (
    <button
      onClick={onClick}
      className={`flex-1 min-w-[140px] p-4 rounded-xl border bg-gradient-to-br ${bg} text-left transition-all hover:shadow-md ${active ? "ring-2 ring-offset-2 ring-plum-500" : ""}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-5 h-5 ${text}`} />
        <span className={`text-sm font-medium ${text}`}>{label}</span>
      </div>
      <div className="text-3xl font-extrabold text-slate-900 tabular-nums">{value}</div>
    </button>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  return formatDateTime(iso) || "—";
}

export default function Feedback() {
  // Otevrena fotka v prohlizeci pres celou obrazovku (8.9.2026): { fotky: [url], index }
  const [lightbox, setLightbox] = useState(null);
  const { data, loading, error, refetch } = useApiData("/api/feedback");
  // Default filtr: "active" = otevřené + v řešení (co potřebuje pozornost)
  const [filterStatus, setFilterStatus] = useState("active");
  const [filterTyp, setFilterTyp] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [busy, setBusy] = useState(null);

  const entries = data?.entries || [];
  const stats = data?.stats || { total: 0, open: 0, in_progress: 0, resolved: 0, wont_fix: 0, typ_bug: 0, typ_navrh: 0, typ_zpetna: 0 };

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (filterStatus === "active") {
        if (e.status !== "open" && e.status !== "in_progress") return false;
      } else if (filterStatus !== "all" && e.status !== filterStatus) return false;
      if (filterTyp !== "all" && e.typ !== filterTyp) return false;
      return true;
    });
  }, [entries, filterStatus, filterTyp]);

  const activeCount = (stats.open || 0) + (stats.in_progress || 0);

  // Vyřešeno / Neopravíme = autorovi odejde zpráva do zvonečku a na Slack, proto
  // se před uložením nabídne vzkaz. Prázdný vzkaz je v pořádku, zpráva odejde i tak.
  const [vzkaz, setVzkaz] = useState(null); // { id, status, text }

  async function changeStatus(id, newStatus, resolutionNote) {
    setBusy(id);
    try {
      const r = await apiFetch("/api/feedback", {
        method: "POST",
        body: JSON.stringify({ action: "update", id, status: newStatus, resolution_note: resolutionNote || undefined }),
      });
      const j = await r.json().catch(() => ({}));
      setVzkaz(null);
      await refetch();
      if (j && j.informovan) toast(`Autor (${j.informovan}) dostal zprávu.`, "success");
    } catch (e) {
      toast("Chyba: " + e.message, "error");
    } finally {
      setBusy(null);
    }
  }

  async function deleteEntry(id) {
    if (!await confirmDialog("Opravdu smazat hlášení? Nelze vrátit.")) return;
    setBusy(id);
    try {
      await apiFetch("/api/feedback", {
        method: "POST",
        body: JSON.stringify({ action: "delete", id }),
      });
      await refetch();
    } catch (e) {
      toast("Chyba: " + e.message, "error");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-4 px-4 py-4 sm:space-y-6 sm:px-5 sm:py-6">
      <GradientHeader
        title="Hlášení uživatelů"
        subtitle="Bugy, návrhy na zlepšení, zpětná vazba na práci s aplikací."
        icon={MessageSquare}
        loading={loading}
        akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: loading }]}
      />

      {/* KPI dlazdice — status (klik filtruje, default Aktivní = otevřené + v řešení) */}
      <div className="flex gap-3 flex-wrap">
        <KpiBox icon={AlertTriangle} color="indigo" label="Aktivní" value={activeCount} onClick={() => setFilterStatus("active")} active={filterStatus === "active"} />
        <KpiBox icon={Clock} color="rose" label="Otevřené" value={stats.open} onClick={() => setFilterStatus("open")} active={filterStatus === "open"} />
        <KpiBox icon={RefreshCw} color="amber" label="V řešení" value={stats.in_progress} onClick={() => setFilterStatus("in_progress")} active={filterStatus === "in_progress"} />
        <KpiBox icon={CheckCircle} color="emerald" label="Vyřešeno" value={stats.resolved} onClick={() => setFilterStatus("resolved")} active={filterStatus === "resolved"} />
        <KpiBox icon={XCircle} color="slate" label="Neřešit" value={stats.wont_fix} onClick={() => setFilterStatus("wont_fix")} active={filterStatus === "wont_fix"} />
        <KpiBox icon={MessageSquare} color="slate" label="Vše" value={stats.total} onClick={() => setFilterStatus("all")} active={filterStatus === "all"} />
      </div>

      {/* Filter typ — pills */}
      <div className="flex gap-2 flex-wrap items-center">
        <span className="text-sm text-slate-500 font-medium">Typ:</span>
        <button
          onClick={() => setFilterTyp("all")}
          className={`min-h-[44px] px-3 py-1.5 rounded-full text-sm font-medium border transition-colors sm:min-h-0 ${filterTyp === "all" ? "bg-plum-100 text-plum-700 border-plum-300" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
        >Vše ({stats.total})</button>
        <button
          onClick={() => setFilterTyp("bug")}
          className={`min-h-[44px] px-3 py-1.5 rounded-full text-sm font-medium border inline-flex items-center gap-1.5 transition-colors sm:min-h-0 ${filterTyp === "bug" ? "bg-rose-100 text-rose-700 border-rose-300" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
        ><Bug className="w-3.5 h-3.5" /> Bugy ({stats.typ_bug})</button>
        <button
          onClick={() => setFilterTyp("navrh")}
          className={`min-h-[44px] px-3 py-1.5 rounded-full text-sm font-medium border inline-flex items-center gap-1.5 transition-colors sm:min-h-0 ${filterTyp === "navrh" ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
        ><Lightbulb className="w-3.5 h-3.5" /> Návrhy ({stats.typ_navrh})</button>
        <button
          onClick={() => setFilterTyp("zpetna_vazba")}
          className={`min-h-[44px] px-3 py-1.5 rounded-full text-sm font-medium border inline-flex items-center gap-1.5 transition-colors sm:min-h-0 ${filterTyp === "zpetna_vazba" ? "bg-sky-100 text-sky-700 border-sky-300" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
        ><MessageSquare className="w-3.5 h-3.5" /> Zpětná vazba ({stats.typ_zpetna})</button>
      </div>

      {/* Tabulka */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading && entries.length === 0 ? (
          <div className="p-12 text-center text-slate-500">Načítám…</div>
        ) : error ? (
          <div className="p-6 text-center text-rose-600">{error.message || "Chyba"}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            Žádná hlášení.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((e) => {
              const typMeta = TYP_META[e.typ] || { label: e.typ, icon: MessageSquare, color: "slate" };
              const statusMeta = STATUS_META[e.status] || { label: e.status, color: "slate" };
              const TypIcon = typMeta.icon;
              const isExpanded = expandedId === e.id;
              const isBusy = busy === e.id;
              return (
                <div key={e.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : e.id)}
                      className="p-1 rounded hover:bg-slate-200 text-slate-400 mt-0.5"
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <div className={`p-2 rounded-xl ${typMeta.color === "rose" ? "bg-rose-100" : typMeta.color === "amber" ? "bg-amber-100" : "bg-sky-100"}`}>
                      <TypIcon className={`w-5 h-5 ${typMeta.color === "rose" ? "text-rose-600" : typMeta.color === "amber" ? "text-amber-600" : "text-sky-600"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{typMeta.label}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${STATUS_PILL[statusMeta.color]}`}>{statusMeta.label}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${e.zdroj === "portal" ? "bg-sky-50 text-sky-700 border-sky-200" : e.zdroj === "klub" ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-brand-50 text-brand-700 border-brand-200"}`}>
                          {e.zdroj === "portal" ? "Klientská sekce" : e.zdroj === "klub" ? "Klub" : "Admin"}
                        </span>
                        <Oddelovac />
                        <span className="text-sm text-slate-700 font-medium">{e.user_name || e.user_email}</span>
                        {e.user_role && <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{e.user_role}</span>}
                        <span className="text-sm text-slate-400 ml-auto">{formatDate(e.timestamp)}</span>
                      </div>
                      <div className="text-sm text-slate-800 mt-1.5 line-clamp-2">{e.popis}</div>
                      {e.stranka && (
                        <div className="text-xs text-slate-500 mt-1 font-mono">{e.stranka}</div>
                      )}
                      {isExpanded && (
                        <div className="mt-3 pt-3 border-t border-slate-200 space-y-3">
                          <div>
                            <div className="text-xs font-semibold text-slate-500 mb-1">PLNÝ POPIS</div>
                            <div className="text-sm text-slate-800 whitespace-pre-wrap">{e.popis}</div>
                          </div>
                          {e.screenshot_url && (
                            <div>
                              <div className="text-xs font-semibold text-slate-500 mb-1">FOTKY</div>
                              {/* 20.8.2026: jedno hlaseni muze nest az 3 fotky, URL jsou
                                  v jednom sloupci oddelene " | ". Stare zaznamy = 1 URL. */}
                              <div className="flex flex-wrap gap-2">
                                {(() => {
                                  const vsechny = String(e.screenshot_url).split(" | ").filter(Boolean);
                                  const jenObrazky = vsechny.filter((x) => /^\/api\/feedback-screenshot/.test(x) || /i\.imgur\.com|\.(png|jpe?g|gif|webp)(\?|$)/i.test(x));
                                  return vsechny.map((u, i) => (
                                  (/^\/api\/feedback-screenshot/.test(u) || /i\.imgur\.com|\.(png|jpe?g|gif|webp)(\?|$)/i.test(u)) ? (
                                    // 8.9.2026: fotka se otevre v prohlizeci uvnitr aplikace, ne na nove karte.
                                    // Na telefonu z holeho obrazku v nove karte nevedla cesta zpet.
                                    <button key={i} type="button" title="Otevřít v plné velikosti"
                                      onClick={() => setLightbox({ fotky: jenObrazky, index: Math.max(0, jenObrazky.indexOf(u)) })}>
                                      <img src={u} alt={`Fotka ${i + 1}`} className="max-h-64 rounded-xl border border-slate-200 hover:opacity-90" />
                                    </button>
                                  ) : (
                                    <a key={i} href={u} target="_blank" rel="noreferrer" className="text-plum-600 hover:underline text-sm inline-flex items-center gap-1">
                                      {u} <ExternalLink className="w-3 h-3" />
                                    </a>
                                  )
                                  ));
                                })()}
                              </div>
                            </div>
                          )}
                          {e.resolution_note && (
                            <div>
                              <div className="text-xs font-semibold text-slate-500 mb-1">POZNÁMKA K ŘEŠENÍ</div>
                              <div className="text-sm text-slate-800">{e.resolution_note}</div>
                              {e.resolved_at && <div className="text-xs text-slate-500 mt-1">{e.resolved_by}, {formatDate(e.resolved_at)}</div>}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2 pt-2">
                            <span className="text-sm text-slate-500 self-center mr-1">Změnit stav:</span>
                            {Object.keys(STATUS_META).map((s) => (
                              <button
                                key={s}
                                onClick={() => (["resolved", "wont_fix"].includes(s)
                                  ? setVzkaz({ id: e.id, status: s, text: e.resolution_note || "" })
                                  : changeStatus(e.id, s))}
                                disabled={isBusy || e.status === s}
                                className={`text-xs px-3 py-1.5 rounded-xl border font-medium ${e.status === s ? "bg-slate-200 text-slate-500 cursor-default" : STATUS_PILL[STATUS_META[s].color] + " hover:opacity-80"}`}
                              >
                                {STATUS_META[s].label}
                              </button>
                            ))}
                            {vzkaz && vzkaz.id === e.id && (
                              <div className="w-full mt-2 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
                                <div className="text-sm font-semibold text-emerald-900">
                                  Vzkaz autorovi ({e.user_name || e.user_email}), nepovinné
                                </div>
                                <textarea
                                  autoFocus
                                  value={vzkaz.text}
                                  onChange={(ev) => setVzkaz({ ...vzkaz, text: ev.target.value })}
                                  placeholder="Např. Opraveno, po obnovení stránky už tlačítko funguje. Díky za nahlášení."
                                  className="w-full mt-2 text-sm border border-emerald-200 rounded-lg px-3 py-2 min-h-[70px] focus:outline-none focus:border-emerald-400"
                                />
                                <div className="flex flex-wrap gap-2 mt-2">
                                  <button
                                    onClick={() => changeStatus(vzkaz.id, vzkaz.status, vzkaz.text.trim())}
                                    disabled={isBusy}
                                    className="text-xs px-3 py-1.5 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50"
                                  >
                                    {isBusy ? "Ukládám…" : `Označit jako ${STATUS_META[vzkaz.status].label} a poslat`}
                                  </button>
                                  <button onClick={() => setVzkaz(null)} className="text-xs px-3 py-1.5 rounded-xl border border-slate-300 text-slate-600 font-medium">
                                    Zrušit
                                  </button>
                                </div>
                                <div className="text-[11px] text-emerald-700/80 mt-1.5">
                                  Autorovi přijde notifikace do zvonečku a Slack zpráva.
                                </div>
                              </div>
                            )}
                            <button
                              onClick={() => deleteEntry(e.id)}
                              disabled={isBusy}
                              className="text-xs px-3 py-1.5 rounded-xl border border-rose-200 text-rose-600 hover:bg-rose-50 font-medium inline-flex items-center gap-1 ml-auto"
                            >
                              <Trash2 className="w-3 h-3" /> Smazat
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {lightbox && (
        <FotkaLightbox
          fotky={lightbox.fotky}
          index={lightbox.index}
          onIndex={(i) => setLightbox((s) => (s ? { ...s, index: i } : s))}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
